import React, { useEffect } from 'react';
import { Box, Card, CardContent, Typography, Alert, LinearProgress, Paper, IconButton, Chip, Divider, Dialog } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useTranslation } from 'react-i18next';

function SectorDetail({ selectedSector, onClose, getSaturationColor }) {
  const { t, i18n } = useTranslation();
  
  // Debug log để kiểm tra data changes  
  useEffect(() => {
    if (selectedSector) {
      console.log('SectorDetail received new data:', selectedSector);
      console.log('Sector name:', selectedSector.sector_name);
      console.log('Saturation score:', selectedSector.saturation_score);
      console.log('Components:', selectedSector.components);
    }
  }, [selectedSector]);

  if (!selectedSector) return null;

  // Get sector name based on current language
  const sectorName = i18n.language === 'vi' 
    ? selectedSector.sector_name_vi 
    : selectedSector.sector_name;

  // Log khi render
  console.log('RENDERING SectorDetail with:', selectedSector.sector_name, selectedSector.saturation_score);

  const getRecommendationWithIcon = (score) => {
    if (score >= 70) return { text: t('saturation.sectorDetail.avoidReduce'), icon: <WarningIcon />, severity: 'error' };
    if (score >= 50) return { text: t('saturation.sectorDetail.cautionMonitor'), icon: <InfoIcon />, severity: 'warning' };
    if (score >= 30) return { text: t('saturation.sectorDetail.selectiveWatch'), icon: <InfoIcon />, severity: 'info' };
    return { text: t('saturation.sectorDetail.attractiveAccumulate'), icon: <CheckCircleIcon />, severity: 'success' };
  };

  return (
    <Dialog 
      open={!!selectedSector} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      {selectedSector && (
        <Card 
          elevation={0} 
          sx={{ 
            border: `1px solid ${getSaturationColor(selectedSector.saturation_score).color}`,
            overflow: 'hidden',
            borderRadius: 3
          }}
        >
          {/* Professional Header */}
          <Box sx={{
            background: `linear-gradient(135deg, ${getSaturationColor(selectedSector.saturation_score).color}15, ${getSaturationColor(selectedSector.saturation_score).color}05)`,
            borderBottom: `1px solid ${getSaturationColor(selectedSector.saturation_score).color}30`,
            p: 3
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <AssessmentIcon sx={{ color: getSaturationColor(selectedSector.saturation_score).color, fontSize: '1.5rem' }} />
                  <Typography variant="h5" fontWeight="700" color="text.primary">
                    {sectorName}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography 
                      variant="h2" 
                      fontWeight="800" 
                      color={getSaturationColor(selectedSector.saturation_score).color}
                      sx={{ lineHeight: 1 }}
                    >
                      {selectedSector.saturation_score}
                    </Typography>
                    <Typography variant="h5" color="text.secondary" fontWeight="600">
                      /100
                    </Typography>
                  </Box>
                  
                  <Chip
                    label={getSaturationColor(selectedSector.saturation_score).text}
                    sx={{
                      backgroundColor: getSaturationColor(selectedSector.saturation_score).color,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      px: 1
                    }}
                  />
                </Box>
              </Box>
              
              <IconButton 
                onClick={onClose} 
                size="small"
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <CardContent sx={{ p: 3 }}>
            {/* Investment Recommendation */}
            <Alert
              severity={getRecommendationWithIcon(selectedSector.saturation_score).severity}
              icon={getRecommendationWithIcon(selectedSector.saturation_score).icon}
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Typography variant="body1" fontWeight="600">
                  {t('saturation.sectorDetail.investmentRecommendation')}
                </Typography>
                <Typography variant="subtitle1" fontWeight="700">
                  {getRecommendationWithIcon(selectedSector.saturation_score).text}
                </Typography>
              </Box>
            </Alert>

            <Divider sx={{ mb: 3 }} />

            {/* Component Analysis Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box sx={{ 
                width: 4, 
                height: 24, 
                backgroundColor: getSaturationColor(selectedSector.saturation_score).color,
                borderRadius: 1
              }} />
              <Typography variant="h6" fontWeight="700" color="text.primary">
                {t('saturation.sectorDetail.componentAnalysis')}
              </Typography>
            </Box>

            {/* Professional Component Flexbox */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 3,
              width: '100%'
            }}>
              {Object.values(selectedSector.components || {}).map((comp, idx) => (
                <Box key={idx} sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 2.5, 
                      height: '100%',
                      borderRadius: 2,
                      border: '1px solid rgba(0,0,0,0.06)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        elevation: 4,
                        transform: 'translateY(-2px)',
                        borderColor: getSaturationColor(comp.percentage).color + '40'
                      }
                    }}
                  >
                    {/* Component Header */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="overline" color="text.secondary" fontWeight="600">
                        {comp.component}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="700" color="text.primary" sx={{ mt: 0.5 }}>
                        {comp.name}
                      </Typography>
                    </Box>

                    {/* Progress Section */}
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(comp.percentage, 100)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(0,0,0,0.08)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: comp.percentage >= 70 ? '#d32f2f' : comp.percentage >= 50 ? '#f57c00' : '#388e3c',
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>

                    {/* Score Display */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography variant="h5" fontWeight="700" color="text.primary">
                        {Number(comp.score).toFixed(1)}
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                          /{Number(comp.max_score).toFixed(0)}
                        </Typography>
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="600" color={comp.percentage >= 70 ? '#d32f2f' : comp.percentage >= 50 ? '#f57c00' : '#388e3c'}>
                        {Number(comp.percentage).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Dialog>
  );
}

export default SectorDetail;